import { createError } from '@common/neo.tax';

import {
  GET_ASC_PROJECT_TEAM_MEMBERS,
  GetAscProjectTeamMembersQuery,
  GetAscProjectTeamMembersQueryVariables,
  AscProjectTeamMemberSortFields,
  SortDirection,
  useCursorPaginatedQuery,
  PeriodName,
} from '@neo.tax/shared';

export type AscProjectTeamMember = GetAscProjectTeamMembersQuery['getAscProjectTeamMembers']['edges'][number]['node'];

export type UseAscProjectTeamMembersResult = {
  loading: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  fetchMore: () => Promise<void>;
  ascProjectTeamMembers: AscProjectTeamMember[] | null;
};

/**
 * Hook used to get all ASC project team members in alphabetical order
 */
export const useAscProjectTeamMembers = ({
  ascProjectId,
  debounceKey,
  fiscalYearId,
  hasAllocation,
  numElementsPerFetch = 30,
  periodName,
  searchKeyword,
}: {
  debounceKey?: string;
  hasAllocation?: boolean | undefined;
  periodName: PeriodName;
  ascProjectId: string;
  searchKeyword?: string | null;
  fiscalYearId: string;
  numElementsPerFetch?: number;
}): UseAscProjectTeamMembersResult => {
  const filter = {
    allocationPercent: hasAllocation === undefined ? undefined : hasAllocation ? { gt: 0 } : { eq: 0 },
    contributorName: !searchKeyword || searchKeyword.length === 0 ? undefined : { iLike: `%${searchKeyword}%` },
  };
  const sorting = {
    field: AscProjectTeamMemberSortFields.ContributorName,
    direction: SortDirection.Asc,
  };

  const { data, error, hasNextPage, isLoadingMore, loading, onLoadMoreItems } = useCursorPaginatedQuery<
    GetAscProjectTeamMembersQuery,
    GetAscProjectTeamMembersQueryVariables
  >(
    GET_ASC_PROJECT_TEAM_MEMBERS,
    {
      variables: {
        ascProjectId,
        fiscalYearId,
        periodName,
        filter,
        sorting,
      },
      context: {
        debounceKey,
      },
    },
    {
      getConnection: (data) => data.getAscProjectTeamMembers,
      numElementsPerFetch,
    },
  );

  const ascProjectTeamMembers = data?.getAscProjectTeamMembers?.edges.map((edge) => edge.node) ?? null;

  if (loading) {
    return {
      loading: true,
      isLoadingMore: false,
      hasNextPage,
      ascProjectTeamMembers,
      fetchMore: onLoadMoreItems,
    };
  }
  if (error) {
    throw error;
  }
  if (!data) {
    throw createError(
      'MissingAscProjectTeamMembers',
      `ascProjectId: ${ascProjectId}, fiscalYearId: ${fiscalYearId}, periodName: ${periodName}, searchKeyword: ${searchKeyword}, hasAllocation: ${hasAllocation}`,
    );
  }

  return {
    loading,
    isLoadingMore,
    hasNextPage,
    ascProjectTeamMembers,
    fetchMore: onLoadMoreItems,
  };
};
